import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { resolveRole, type AppRole, type DecodedIdToken } from "./oidcConfig";
import { userManager } from "./userManager";

/**
 * Replaces both src/app/api/auth/callback/page.tsx (the role-router) and
 * the NextAuth catch-all's callback handling in one place.
 */
export function CallbackPage() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    const navigateToRole = (roles: string[] | undefined) => {
      const role = resolveRole(roles);
      const destinations: Record<AppRole, string> = {
        BLOOD_BANK: "/blood_bank",
        HOSPITAL: "/hospital",
        IT_SUPPORT: "/it_support",
        CAMP_ORGANIZER: "/campaign_org",
        DONOR: "/donor",
      };
      navigate(destinations[role], { replace: true });
    };

    userManager
      .signinRedirectCallback()
      .then((user) => {
        navigateToRole((user.profile as DecodedIdToken).roles);
      })
      .catch(async (err) => {
        // Fallback: check if the user is already authenticated (e.g. state was already consumed or page was refreshed)
        const existingUser = await userManager.getUser();
        if (existingUser && !existingUser.expired) {
          navigateToRole((existingUser.profile as DecodedIdToken).roles);
          return;
        }

        console.error("OIDC callback error:", err);
        setError(err instanceof Error ? err.message : "Sign-in failed");
      });
  }, [navigate]);

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-8 text-center">
        <div className="max-w-md rounded-lg border border-red-200 bg-red-50 p-6 text-red-800 shadow-sm">
          <h2 className="mb-2 text-lg font-semibold">Sign-in Error</h2>
          <p className="mb-4 text-sm text-red-600">
            {error === "No matching state found in storage"
              ? "Your login session has expired or was already completed. Please try signing in again."
              : error}
          </p>
          <div className="flex flex-col gap-2 sm:flex-row sm:justify-center">
            <button
              onClick={() => userManager.signinRedirect()}
              className="rounded bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 transition-colors"
            >
              Try Signing In Again
            </button>
            <a
              href="/"
              className="rounded border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Return Home
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center">
      <p className="text-gray-500">Signing you in…</p>
    </div>
  );
}
